import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import usePersistentState from '../utils/usePersistentState';
import toast from 'react-hot-toast';
import { teamAPI, authAPI, requirementAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button, Input, Textarea, Modal, Badge, EmptyState, Spinner, ConfirmModal} from '../components/UI';
import RichTextEditor from '../components/RichTextEditor';
import SafeHtml from '../components/SafeHtml';
import HistoryPanel from '../components/HistoryPanel';
import GlobalHistoryPanel from '../components/GlobalHistoryPanel';

// ── Candidate status colours ──────────────────────────────────────────────────
const PIPELINE_COLORS_FN = (isDark) => ({
  Applied:     { bar: '#94a3b8', pill: { bg: isDark ? 'rgba(100,116,139,0.2)' : '#f1f5f9', color: isDark ? '#cbd5e1' : '#475569', border: isDark ? 'rgba(100,116,139,0.3)' : '#e2e8f0' } },
  Shortlisted: { bar: '#fbbf24', pill: { bg: isDark ? 'rgba(245,158,11,0.18)' : '#fef3c7', color: isDark ? '#fcd34d' : '#d97706', border: isDark ? 'rgba(245,158,11,0.3)' : '#fde68a' } },
  Interview:   { bar: '#60a5fa', pill: { bg: isDark ? 'rgba(59,130,246,0.18)' : '#dbeafe', color: isDark ? '#93c5fd' : '#2563eb', border: isDark ? 'rgba(59,130,246,0.3)' : '#bfdbfe' } },
  Hired:       { bar: '#4ade80', pill: { bg: isDark ? 'rgba(34,197,94,0.18)' : '#dcfce7', color: isDark ? '#86efac' : '#16a34a', border: isDark ? 'rgba(34,197,94,0.3)' : '#bbf7d0' } },
  Rejected:    { bar: '#f87171', pill: { bg: isDark ? 'rgba(239,68,68,0.18)' : '#fee2e2', color: isDark ? '#fca5a5' : '#dc2626', border: isDark ? 'rgba(239,68,68,0.3)' : '#fecaca' } },
});
const STATUS_ORDER = ['Applied', 'Screening', 'Shortlisted', 'Interview', 'Hold', 'Dropped', 'Hired', 'Rejected'];

const SEL_STYLE = {
  background: 'var(--bg-input)',
  border: '1px solid var(--border-input)',
  color: 'var(--text-primary)',
  borderRadius: 8,
  padding: '4px 8px',
  fontSize: 12,
  outline: 'none',
  cursor: 'pointer',
};

// ── Team avatar colour palette ────────────────────────────────────────────────
const TEAM_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];
const teamColor = (name) => TEAM_COLORS[(name?.charCodeAt(0) ?? 0) % TEAM_COLORS.length] || TEAM_COLORS[0];

// ── Per-card component with its own local state ───────────────────────────────
function RequirementCard({ r, allTeams, canEdit, onSaved, onFullEdit }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const PIPELINE_COLORS = PIPELINE_COLORS_FN(isDark);

  const [status,   setStatus]   = useState(r.status   || 'Open');
  const [priority, setPriority] = useState(r.priority || 'Medium');
  const [teamId,   setTeamId]   = useState(r.teamId   || '');
  const [saving,   setSaving]   = useState(false);

  const isDirty = status !== (r.status || 'Open') ||
                  priority !== (r.priority || 'Medium') ||
                  teamId !== (r.teamId || '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await requirementAPI.update(r.id, { status, priority, teamId: teamId || null });
      toast.success('Saved!');
      onSaved(r.id, { status, priority, teamId: teamId || null });
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const total = r.totalCandidates || 0;

  // Status badge styles
  const statusBadgeStyle = (s) => {
    if (s === 'Open')   return { background: isDark ? 'rgba(34,197,94,0.2)' : '#dcfce7', color: isDark ? '#86efac' : '#16a34a', border: `1px solid ${isDark ? 'rgba(34,197,94,0.3)' : '#bbf7d0'}` };
    if (s === 'Closed') return { background: isDark ? 'rgba(239,68,68,0.2)' : '#fee2e2', color: isDark ? '#fca5a5' : '#dc2626', border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : '#fecaca'}` };
    return { background: isDark ? 'rgba(245,158,11,0.2)' : '#fef3c7', color: isDark ? '#fcd34d' : '#d97706', border: `1px solid ${isDark ? 'rgba(245,158,11,0.3)' : '#fde68a'}` };
  };

  const priorityBadgeStyle = (p) => {
    if (p === 'Urgent') return { background: isDark ? 'rgba(239,68,68,0.2)' : '#fee2e2', color: isDark ? '#fca5a5' : '#dc2626', border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : '#fecaca'}` };
    if (p === 'High')   return { background: isDark ? 'rgba(249,115,22,0.2)' : '#ffedd5', color: isDark ? '#fdba74' : '#ea580c', border: `1px solid ${isDark ? 'rgba(249,115,22,0.3)' : '#fed7aa'}` };
    if (p === 'Medium') return { background: isDark ? 'rgba(245,158,11,0.2)' : '#fef3c7', color: isDark ? '#fcd34d' : '#d97706', border: `1px solid ${isDark ? 'rgba(245,158,11,0.3)' : '#fde68a'}` };
    return { background: isDark ? 'rgba(100,116,139,0.2)' : '#f1f5f9', color: isDark ? '#cbd5e1' : '#475569', border: `1px solid ${isDark ? 'rgba(100,116,139,0.3)' : '#e2e8f0'}` };
  };

  return (
    <div style={{
      background: 'var(--bg-raised)',
      border: isDirty ? '1px solid rgba(245,158,11,0.5)' : '1px solid var(--border-subtle)',
      borderRadius: 12,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      transition: 'border-color 0.2s',
    }}>

      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-primary)' }} className="truncate">{r.jobTitle}</p>
          <p style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>🏢 {r.client?.clientName}</p>
        </div>
        {saving
          ? <span className="text-xs text-indigo-400 animate-pulse flex-shrink-0">Saving…</span>
          : isDirty && <span className="text-xs text-amber-400 flex-shrink-0">Unsaved</span>
        }
      </div>

      {/* Status & Priority */}
      <div className="flex gap-2 flex-wrap items-center">
        {canEdit ? (
          <>
            <select value={status} disabled={saving} onChange={e => setStatus(e.target.value)} style={SEL_STYLE}>
              {['Open', 'Closed', 'On Hold'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={priority} disabled={saving} onChange={e => setPriority(e.target.value)} style={SEL_STYLE}>
              {['Low', 'Medium', 'High', 'Urgent'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </>
        ) : (
          <>
            <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 99, fontWeight: 500, ...statusBadgeStyle(r.status) }}>{r.status}</span>
            <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 99, fontWeight: 500, ...priorityBadgeStyle(r.priority) }}>{r.priority}</span>
          </>
        )}
      </div>

      {/* Re-assign team */}
      {canEdit && allTeams.length > 0 && (
        <div className="flex items-center gap-2">
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0 }}>Team:</span>
          <select value={teamId} disabled={saving}
            onChange={e => setTeamId(e.target.value)}
            style={{ ...SEL_STYLE, flex: 1 }}
          >
            <option value="">— Unassign —</option>
            {allTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      )}

      {/* Meta */}
      <div className="flex gap-3 flex-wrap" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {r.location && <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 }}>📍 {r.location}</span>}
        <span>💼 {r.minExperience}–{r.maxExperience} yrs</span>
        <span>🧑‍💼 {r.openings} opening{r.openings !== 1 ? 's' : ''}</span>
      </div>

      {/* Pipeline bar — taller, more impressive */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pipeline</p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{total} total</p>
        </div>
        {total === 0 ? (
          <div style={{ height: 6, background: isDark ? '#334155' : '#e2e8f0', borderRadius: 99 }} />
        ) : (
          <div style={{ display: 'flex', height: 6, borderRadius: 99, overflow: 'hidden', gap: 1 }}>
            {STATUS_ORDER.filter(s => r.pipeline[s] > 0).map(s => (
              <div key={s}
                style={{ width: `${(r.pipeline[s] / total) * 100}%`, background: PIPELINE_COLORS[s].bar, height: '100%', borderRadius: 2 }}
                title={`${s}: ${r.pipeline[s]}`} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {STATUS_ORDER.filter(s => r.pipeline[s] > 0).map(s => (
            <span key={s} style={{
              fontSize: 11,
              padding: '2px 7px',
              borderRadius: 99,
              fontWeight: 500,
              border: '1px solid',
              background: PIPELINE_COLORS[s].pill.bg,
              color: PIPELINE_COLORS[s].pill.color,
              borderColor: PIPELINE_COLORS[s].pill.border,
            }}>
              {s} {r.pipeline[s]}
            </span>
          ))}
          {total === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No candidates yet</span>}
        </div>
      </div>

      {/* Actions row */}
      {canEdit && (
        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 8 }}
          className="flex items-center justify-between gap-2">
          <Button variant="outline" size="xs" onClick={() => onFullEdit(r)}>✏️ Full Edit</Button>
          <Button variant="primary" size="xs" disabled={saving || !isDirty} loading={saving} onClick={handleSave}>
            💾 Save
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Team Dashboard Modal ──────────────────────────────────────────────────────
function TeamDashboardModal({ teamId, onClose, allTeams = [] }) {
  const { isSuperAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editReq, setEditReq] = useState(null);
  const [reqForm, setReqForm] = useState({});
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    teamAPI.getDashboard(teamId)
      .then(({ data: res }) => setData(res))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [teamId]);

  const refreshSilent = (id) =>
    teamAPI.getDashboard(id).then(({ data: res }) => setData(res)).catch(() => {});

  const handleSaved = (reqId, patch) => {
    setData(prev => ({
      ...prev,
      requirements: prev.requirements.map(r => r.id === reqId ? { ...r, ...patch } : r),
    }));
  };

  const openFullEdit = (r) => {
    setReqForm({
      jobTitle: r.jobTitle || '', status: r.status || 'Open', priority: r.priority || 'Medium',
      openings: r.openings || 1, location: r.location || '', teamId: r.teamId || '',
      minExperience: r.minExperience ?? 0, maxExperience: r.maxExperience ?? 0,
    });
    setEditReq(r);
  };

  const saveFullEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await requirementAPI.update(editReq.id, reqForm);
      toast.success('Requirement updated!');
      setEditReq(null);
      refreshSilent(teamId);
    } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
    finally { setSaving(false); }
  };

  if (!teamId) return null;
  const { team, requirements: allReqs = [], stats = {} } = data || {};
  const requirements = allReqs.filter(r => r.status !== 'Closed');
  const canEdit  = isSuperAdmin();
  const managers   = (team?.members || []).filter(m => m.TeamMember?.roleInTeam === 'manager');
  const recruiters = (team?.members || []).filter(m => m.TeamMember?.roleInTeam === 'recruiter');

  const managerBadgeStyle = {
    fontSize: 12,
    background: isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe',
    color: isDark ? '#93c5fd' : '#2563eb',
    border: `1px solid ${isDark ? 'rgba(59,130,246,0.3)' : '#bfdbfe'}`,
    padding: '2px 8px',
    borderRadius: 99,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  };

  const recruiterBadgeStyle = {
    fontSize: 12,
    background: isDark ? 'rgba(34,197,94,0.2)' : '#dcfce7',
    color: isDark ? '#86efac' : '#16a34a',
    border: `1px solid ${isDark ? 'rgba(34,197,94,0.3)' : '#bbf7d0'}`,
    padding: '2px 8px',
    borderRadius: 99,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  };

  const inputStyle = {
    width: '100%',
    background: 'var(--bg-input)',
    border: '1px solid var(--border-input)',
    color: 'var(--text-primary)',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <>
      <Modal isOpen={!!teamId} onClose={onClose} title={`📊 ${team?.name || 'Team'} Dashboard`} size="xl">
        {loading ? (
          <div className="py-16 flex justify-center"><Spinner /></div>
        ) : (
          <div className="space-y-6">

            {/* ── Team Members ───────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div style={{
                background: isDark ? 'rgba(30,41,59,0.5)' : '#fafafa',
                border: '1px solid var(--border-subtle)',
                borderRadius: 12,
                padding: 16,
                boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
              }}>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Managers · {managers.length}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {managers.length === 0
                    ? <span style={{ color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic' }}>None assigned</span>
                    : managers.map(m => (
                      <span key={m.id} style={managerBadgeStyle}>{m.name}</span>
                    ))}
                </div>
              </div>
              <div style={{
                background: isDark ? 'rgba(30,41,59,0.5)' : '#fafafa',
                border: '1px solid var(--border-subtle)',
                borderRadius: 12,
                padding: 16,
                boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
              }}>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Recruiters · {recruiters.length}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {recruiters.length === 0
                    ? <span style={{ color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic' }}>None assigned</span>
                    : recruiters.map(m => (
                      <span key={m.id} style={recruiterBadgeStyle}>{m.name}</span>
                    ))}
                </div>
              </div>
            </div>

            {/* ── Summary Stats ──────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Requirements', value: stats.totalReqs,       color: 'var(--text-primary)' },
                { label: 'Open',         value: stats.openReqs,        color: '#4ade80' },
                { label: 'Closed',       value: stats.closedReqs,      color: '#f87171' },
                { label: 'On Hold',      value: stats.onHoldReqs,      color: '#fbbf24' },
                { label: 'Candidates',   value: stats.totalCandidates, color: '#818cf8' },
                { label: 'Hired',        value: stats.totalHired,      color: '#34d399' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{
                  background: isDark ? 'rgba(30,41,59,0.5)' : '#fafafa',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 12,
                  padding: '12px 16px',
                  textAlign: 'center',
                  boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
                }}>
                  <p style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1.2 }}>{value ?? 0}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 4 }}>{label}</p>
                </div>
              ))}
            </div>

            {/* ── Requirements List ──────────────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                  Assigned Requirements
                  {requirements.length > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 14, marginLeft: 8 }}>({requirements.length})</span>}
                </h3>
                {!isSuperAdmin() && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>View only — contact SuperAdmin to make changes</span>
                )}
              </div>

              {requirements.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 16px',
                  background: isDark ? 'rgba(30,41,59,0.4)' : '#f8fafc',
                  border: `1px dashed ${isDark ? '#334155' : '#cbd5e1'}`,
                  borderRadius: 12,
                }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No requirements assigned to this team yet.</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>Go to Requirements → Edit → Assign to Team.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {requirements.map(r => (
                    <RequirementCard
                      key={r.id}
                      r={r}
                      allTeams={allTeams}
                      canEdit={canEdit}
                      onSaved={handleSaved}
                      onFullEdit={openFullEdit}
                    />
                  ))}
                </div>
              )}
            </div>

          </div>
        )}
      </Modal>

      {/* ── Full Edit Requirement Modal (SuperAdmin only) ───────────────────── */}
      {isSuperAdmin() && (
        <Modal isOpen={!!editReq} onClose={() => setEditReq(null)}
          title={`✏️ Edit — ${editReq?.jobTitle || 'Requirement'}`} size="md">
          <form onSubmit={saveFullEdit} className="space-y-4">
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Job Title *</label>
              <input value={reqForm.jobTitle} required
                onChange={e => setReqForm({ ...reqForm, jobTitle: e.target.value })}
                style={inputStyle} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Status</label>
                <select value={reqForm.status} onChange={e => setReqForm({ ...reqForm, status: e.target.value })}
                  style={inputStyle}>
                  {['Open', 'Closed', 'On Hold'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Priority</label>
                <select value={reqForm.priority} onChange={e => setReqForm({ ...reqForm, priority: e.target.value })}
                  style={inputStyle}>
                  {['Low', 'Medium', 'High', 'Urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Min Exp (yrs)</label>
                <input type="number" min="0" value={reqForm.minExperience}
                  onChange={e => setReqForm({ ...reqForm, minExperience: Number(e.target.value) })}
                  style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Max Exp (yrs)</label>
                <input type="number" min="0" value={reqForm.maxExperience}
                  onChange={e => setReqForm({ ...reqForm, maxExperience: Number(e.target.value) })}
                  style={inputStyle} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Openings</label>
                <input type="number" min="1" value={reqForm.openings}
                  onChange={e => setReqForm({ ...reqForm, openings: Number(e.target.value) })}
                  style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Location</label>
                <input value={reqForm.location}
                  onChange={e => setReqForm({ ...reqForm, location: e.target.value })}
                  style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Assign to Team</label>
              <select value={reqForm.teamId} onChange={e => setReqForm({ ...reqForm, teamId: e.target.value })}
                style={inputStyle}>
                <option value="">— Unassigned —</option>
                {allTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <Button variant="secondary" type="button" onClick={() => setEditReq(null)}>Cancel</Button>
              <Button type="submit" loading={saving}>Save Changes</Button>
            </div>
          </form>
        </Modal>
      )}

    </>
  );
}

const ROLE_COLOR = { manager: 'info', recruiter: 'success' };

// ── Member card (Manage Members modal) ────────────────────────────────────────
// The remove button sits in the flex row rather than floating over the card, so
// it can never cover the name. Status chips (role, Inactive) share their own
// wrapping row, so a long name truncates cleanly instead of clipping the badge.
// Card and button hover states live in index.css (.member-card).
function MemberCard({ member: m, isDark, onRemove }) {
  const inactive = m.isActive === false;
  const isManager = m.roleInTeam === 'manager';
  const accent = inactive ? '#94a3b8' : isManager ? '#3b82f6' : '#10b981';
  const initials = (m.name || '?').split(' ').filter(Boolean).map(n => n[0]).slice(0, 2).join('').toUpperCase();

  const chip = (bg, color, border) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 10, fontWeight: 700, lineHeight: '16px',
    padding: '1px 8px', borderRadius: 99, whiteSpace: 'nowrap',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    background: bg, color, border: `1px solid ${border}`,
  });

  return (
    <div className="member-card">
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: accent + '1f',
        border: `1.5px solid ${accent}55`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 700, color: accent,
      }}>
        {initials}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p title={m.name} style={{
          fontSize: 13, fontWeight: 600, margin: 0, lineHeight: '18px',
          color: inactive ? 'var(--text-secondary)' : 'var(--text-primary)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {m.name}
        </p>
        <p title={m.email} style={{
          fontSize: 11, color: 'var(--text-muted)', margin: '1px 0 0', lineHeight: '16px',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {m.email}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 7 }}>
          <span style={isManager
            ? chip('rgba(59,130,246,0.12)', isDark ? '#93c5fd' : '#2563eb', 'rgba(59,130,246,0.3)')
            : chip('rgba(16,185,129,0.12)', isDark ? '#6ee7b7' : '#059669', 'rgba(16,185,129,0.3)')}>
            {m.roleInTeam}
          </span>
          {inactive && (
            <span
              title="This user has been deactivated. They stay on the team until removed, but cannot be newly assigned anywhere."
              style={{ ...chip(isDark ? 'rgba(239,68,68,0.14)' : '#fef2f2', isDark ? '#fca5a5' : '#b91c1c', isDark ? 'rgba(239,68,68,0.35)' : '#fecaca'), cursor: 'help' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
              Inactive
            </span>
          )}
        </div>
      </div>

      {onRemove && (
        <button
          type="button"
          className="member-card__remove"
          onClick={onRemove}
          title={`Remove ${m.name || 'member'}`}
          aria-label={`Remove ${m.name || 'member'} from team`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default function Teams() {
  const { isSuperAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [teams, setTeams]           = useState([]);
  const [loading, setLoading]       = useState(true);
  // Kept for this tab — survives leaving the page and coming back.
  const [search, setSearch]         = usePersistentState('teams:search', '');
  const [modalOpen, setModalOpen]   = useState(false);
  const [memberModal, setMemberModal] = useState({ open: false, team: null });
  const [editTeam, setEditTeam]     = useState(null);
  const [form, setForm]             = useState({ name: '', description: '' });
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(null);
  const [allUsers, setAllUsers]     = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [historyPanel, setHistoryPanel] = useState({ open: false, id: null, name: '' });
  const [globalHistory, setGlobalHistory] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await teamAPI.getAll({ search, limit: 50 });
      setTeams(data.teams);
    } catch { toast.error('Failed to load teams'); }
    finally { setLoading(false); }
  }, [search]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    authAPI.getUsers({ limit: 200 }).then(({ data }) => setAllUsers(data.users)).catch(() => {});
  }, []);

  const openCreate = () => { setEditTeam(null); setForm({ name: '', description: '' }); setModalOpen(true); };
  const openEdit = (t) => { setEditTeam(t); setForm({ name: t.name, description: t.description || '' }); setModalOpen(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editTeam) { await teamAPI.update(editTeam.id, form); toast.success('Team updated!'); }
      else          { await teamAPI.create(form);               toast.success('Team created!'); }
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => { setConfirmDelete(id); };

  const handleDeleteConfirmed = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    setDeleting(id);
    try { await teamAPI.delete(id); toast.success('Team deleted'); load(); }
    catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  const openMemberModal = (team) => {
    const existing = (team.members || []).map(m => ({
      userId: m.id,
      roleInTeam: m.TeamMember?.roleInTeam || 'recruiter',
      name: m.name,
      email: m.email,
      role: m.role,
      // A member deactivated after joining stays on the team until removed.
      // Carried so the row can say so — otherwise there is no way to tell
      // which member is the deactivated one.
      isActive: m.isActive !== false,
    }));
    setPendingMembers(existing);
    setMemberModal({ open: true, team });
  };

  const addMember = (userId, roleInTeam) => {
    const user = allUsers.find(u => u.id === userId);
    if (!user) return;
    if (pendingMembers.find(m => m.userId === userId)) { toast.error('Already added'); return; }
    setPendingMembers(prev => [...prev, { userId, roleInTeam, name: user.name, email: user.email, role: user.role }]);
  };

  const removePending = (userId) => setPendingMembers(prev => prev.filter(m => m.userId !== userId));

  const saveMembers = async () => {
    setSaving(true);
    try {
      await teamAPI.setMembers(memberModal.team.id, { members: pendingMembers });
      toast.success('Members updated!');
      setMemberModal({ open: false, team: null });
      load();
    } catch { toast.error('Failed to update members'); }
    finally { setSaving(false); }
  };

  const [addUserId, setAddUserId] = useState('');
  const [addRoleInTeam, setAddRoleInTeam] = useState('recruiter');
  const [dashboardTeamId, setDashboardTeamId] = useState(null);

  // ── Scroll-lock: prevent body scroll when any overlay is open ────────────
  const anyOverlayOpen = modalOpen || memberModal.open || !!dashboardTeamId || historyPanel.open || globalHistory || !!confirmDelete;
  useLayoutEffect(() => {
    if (anyOverlayOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [anyOverlayOpen]);
  // ─────────────────────────────────────────────────────────────────────────

  const memberSelectStyle = {
    background: 'var(--bg-input)',
    border: `1px solid ${isDark ? 'rgba(99,102,241,0.45)' : 'rgba(99,102,241,0.35)'}`,
    color: 'var(--text-primary)',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxShadow: isDark ? '0 0 0 3px rgba(99,102,241,0.12)' : '0 0 0 2px rgba(99,102,241,0.08)',
  };

  const managerBadgeStyle = {
    fontSize: 12,
    background: isDark ? 'rgba(59,130,246,0.15)' : '#dbeafe',
    color: isDark ? '#93c5fd' : '#2563eb',
    border: `1px solid ${isDark ? 'rgba(59,130,246,0.3)' : '#bfdbfe'}`,
    padding: '3px 10px',
    borderRadius: 99,
    fontWeight: 500,
    maxWidth: 140,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  };

  const recruiterBadgeStyle = {
    fontSize: 12,
    background: isDark ? 'rgba(34,197,94,0.15)' : '#dcfce7',
    color: isDark ? '#86efac' : '#16a34a',
    border: `1px solid ${isDark ? 'rgba(34,197,94,0.3)' : '#bbf7d0'}`,
    padding: '3px 10px',
    borderRadius: 99,
    fontWeight: 500,
    maxWidth: 140,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  };

  const reqsBadgeStyle = (count) => count > 0 ? {
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 99,
    background: isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff',
    color: isDark ? '#a5b4fc' : '#4338ca',
    border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`,
  } : {
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 99,
    background: isDark ? 'rgba(100,116,139,0.2)' : '#f1f5f9',
    color: isDark ? '#64748b' : '#94a3b8',
    border: `1px solid ${isDark ? 'rgba(100,116,139,0.2)' : '#e2e8f0'}`,
  };

  return (
    <div className="space-y-6">
      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em', margin: 0 }}>Teams</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
            {teams.length} team{teams.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setGlobalHistory(true)}>🕐 History</Button>
          {isSuperAdmin() && <Button onClick={openCreate}>+ Create Team</Button>}
        </div>
      </div>

      {/* ── Search ───────────────────────────────────────────────────────────── */}
      <div className="max-w-sm w-full" style={{ marginBottom: 4 }}>
        <Input placeholder="Search teams…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <div className="py-16 flex justify-center"><Spinner /></div> : teams.length === 0 ? (
        <EmptyState icon="👥" title="No teams yet" message="Create your first team."
          action={isSuperAdmin() && <Button onClick={openCreate}>Create Team</Button>} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
          {teams.map(team => {
            const managers   = (team.members || []).filter(m => m.TeamMember?.roleInTeam === 'manager');
            const recruiters = (team.members || []).filter(m => m.TeamMember?.roleInTeam === 'recruiter');
            const isHovered = hoveredCard === team.id;
            const accentColor = teamColor(team.name);
            return (
              <div key={team.id}
                style={{
                  background: 'var(--bg-card)',
                  border: `1px solid ${isHovered ? 'var(--border-brand)' : 'var(--border-subtle)'}`,
                  borderRadius: 16,
                  padding: 20,
                  transition: 'all 0.2s',
                  boxShadow: isHovered
                    ? '0 8px 24px rgba(99,102,241,0.12)'
                    : isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onMouseEnter={() => setHoveredCard(team.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* ── Card header: avatar + name + description ── */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                  {/* Team avatar */}
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 16,
                    flexShrink: 0,
                    boxShadow: `0 2px 8px ${accentColor}40`,
                  }}>
                    {team.name?.[0]?.toUpperCase()}
                  </div>
                  {/* Name + description */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.01em' }}>{team.name}</h3>
                    {team.description && (
                      <SafeHtml as="div" html={team.description} className="line-clamp-2" style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 3, lineHeight: 1.4 }} />
                    )}
                  </div>
                  {/* Reqs count chip */}
                  <span style={{ ...reqsBadgeStyle(team.requirementsCount), flexShrink: 0 }}>
                    {team.requirementsCount ?? 0} req{team.requirementsCount !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* ── Member sections ── */}
                <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                  {/* Managers */}
                  <div>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      Managers · {managers.length}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {managers.length === 0
                        ? <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>None assigned</span>
                        : managers.map(m => (
                          <span key={m.id} style={managerBadgeStyle}>{m.name}</span>
                        ))}
                    </div>
                  </div>
                  {/* Recruiters */}
                  <div>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      Recruiters · {recruiters.length}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {recruiters.length === 0
                        ? <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>None assigned</span>
                        : recruiters.map(r => (
                          <span key={r.id} style={recruiterBadgeStyle}>{r.name}</span>
                        ))}
                    </div>
                  </div>
                </div>

                {/* ── Action row ── */}
                <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, marginTop: 4 }}
                  className="flex gap-2 flex-wrap items-center">
                  <Button variant="primary" size="xs" onClick={() => setDashboardTeamId(team.id)}>
                    📊 Dashboard
                  </Button>
                  <Button variant="outline" size="xs" onClick={() => openMemberModal(team)}>
                    👥 Members
                  </Button>
                  <Button variant="ghost" size="xs" tooltip="View activity history"
                    onClick={() => setHistoryPanel({ open: true, id: team.id, name: team.name })}>
                    🕐 History
                  </Button>
                  {isSuperAdmin() && (
                    <>
                      <Button variant="secondary" size="xs" onClick={() => openEdit(team)}>
                        ✏️ Edit
                      </Button>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Button variant="danger" size="xs" loading={deleting === team.id} loadingText="Deleting…" onClick={() => handleDelete(team.id)}>Delete</Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editTeam ? 'Edit Team' : 'Create Team'} size="sm">
        <form onSubmit={handleSave} className="space-y-4">
          <Input label="Team Name *" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} required />
          <RichTextEditor label="Description" value={form.description} rows={4}
            onChange={(html) => setForm({ ...form, description: html })} />
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>{editTeam ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      {/* Team Dashboard Modal */}
      <TeamDashboardModal teamId={dashboardTeamId} onClose={() => setDashboardTeamId(null)} allTeams={teams} />

      {/* Manage Members Modal */}
      <Modal isOpen={memberModal.open} onClose={() => setMemberModal({ open: false, team: null })}
        title={`Members — ${memberModal.team?.name}`} size="lg">
        <div className="space-y-5">
          {isSuperAdmin() && (
            <div style={{
              background: isDark ? 'rgba(99,102,241,0.06)' : '#f5f3ff',
              border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff'}`,
              borderRadius: 12,
              padding: 16,
            }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#a5b4fc' : '#6366f1', marginBottom: 10, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Add Member
              </p>
              <div className="flex gap-2 items-center flex-wrap">
                <select value={addUserId} onChange={e => setAddUserId(e.target.value)}
                  style={{ ...memberSelectStyle, flex: 1, minWidth: 0 }}>
                  <option value="">— Select user to add —</option>
                  {allUsers.filter(u => u.role !== 'SuperAdmin').map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                  ))}
                </select>
                <div style={{
                  display: 'flex', gap: 3,
                  background: isDark ? 'rgba(15,23,42,0.7)' : '#e8e5f8',
                  border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : '#c7d2fe'}`,
                  borderRadius: 9,
                  padding: 3,
                }}>
                  {['manager', 'recruiter'].map(role => (
                    <button key={role} onClick={() => setAddRoleInTeam(role)}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        background: addRoleInTeam === role
                          ? (role === 'manager' ? '#3b82f6' : '#10b981')
                          : 'transparent',
                        color: addRoleInTeam === role ? '#fff'
                          : isDark ? '#94a3b8' : '#6366f1',
                        textTransform: 'capitalize',
                        letterSpacing: '0.02em',
                      }}>
                      {role}
                    </button>
                  ))}
                </div>
                <Button onClick={() => { addMember(addUserId, addRoleInTeam); setAddUserId(''); }}
                  disabled={!addUserId}>
                  Add
                </Button>
              </div>
            </div>
          )}

          <div style={{ maxHeight: 340, overflowY: 'auto', paddingRight: 2 }}>
            {pendingMembers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '36px 0' }}>
                <div style={{ fontSize: 44, marginBottom: 12, opacity: 0.5 }}>👥</div>
                <p style={{ color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, margin: 0 }}>No members yet</p>
                <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 5, opacity: 0.8 }}>
                  {isSuperAdmin() ? 'Add someone using the form above' : 'No members assigned to this team'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10 }}>
                {pendingMembers.map(m => (
                  <MemberCard key={m.userId} member={m} isDark={isDark}
                    onRemove={isSuperAdmin() ? () => removePending(m.userId) : null} />
                ))}
              </div>
            )}
          </div>

          {isSuperAdmin() && (
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setMemberModal({ open: false, team: null })}>Cancel</Button>
              <Button onClick={saveMembers} loading={saving}>Save Members</Button>
            </div>
          )}
        </div>
      </Modal>

      {/* ── Per-entity History Panel ── */}
      {historyPanel.open && (
        <HistoryPanel
          entityId={historyPanel.id}
          entityName={historyPanel.name}
          onClose={() => setHistoryPanel({ open: false, id: null, name: '' })}
        />
      )}

      {/* ── Global History Feed ── */}
      {globalHistory && (
        <GlobalHistoryPanel
          module="Team"
          onClose={() => setGlobalHistory(false)}
        />
      )}
            {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => setConfirmDelete(null)} />
          <div style={{ position: 'relative', background: isDark ? '#1e2535' : '#ffffff', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: isDark ? '#f1f5f9' : '#111827' }}>Delete Team?</h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: isDark ? '#94a3b8' : '#6b7280' }}>This will permanently remove this team and all its members.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #d1d5db', background: 'transparent', color: isDark ? '#f1f5f9' : '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeleteConfirmed} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
